import { EHttpMethods } from "../../../../enums";
import { AUTH_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { LIST_ORGANIZATIONS_URL, ORGANISATIONS_ADMIN_URL } from "./constants";
import {
  uploadFile,
  uploadFileWithPresignedUrl,
} from "../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../s3Integration/enums";
import {
  IAddOrganizationDocumentationBody,
  IOrganizationBankInfoResponse,
  IOrganizationDocumentationResponse,
  IOrganizationTierResponse,
} from "./models";
import { IAddNewUserBody, IClientAdminResponse } from "./types";

// eslint-disable-next-line no-unused-vars
const defaultProvide = async () => await getToken();

export const getAllOrganizations = (
  page = 0,
  size = 10,
  searchValue = ""
): Promise<any> => {
  const url = {
    get value() {
      if (searchValue === "") {
        return `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?page=${page}&size=${size}&sort=createdAt,DESC`;
      } else {
        const encoded = searchValue.replace("+", "%2B");
        return `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?searchKeyword=${encoded}&page=${page}&size=${size}&sort=createdAt,DESC`;
      }
    },
  };

  const descriptor = new ApiServiceManager(
    url.value,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addNewOrganization = (body: any): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addNewOrganizationUser = (
  body: IAddNewUserBody
): Promise<IClientAdminResponse> => {
  const url = `${AUTH_BASE_URL}${ORGANISATIONS_ADMIN_URL}/organization-admin-sign-up`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const editOrganization = (code: string, body: any): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationAPI = (
  code: string,
  body: any
): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganization = (code: string): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationPaymentMethods = (code: string): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/paymentTypes`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationBanks = (
  code: string
): Promise<IOrganizationBankInfoResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/banks`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationDocumentations = (
  code: string
): Promise<IOrganizationDocumentationResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationTiers = (
  code: string
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates/active/details`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Business self-registration — creates the org as PENDING, grants the caller CLIENT_ADMIN. */
export const selfRegisterOrganization = (body: {
  code: string;
  name: string;
  logoUrl?: string;
  websiteUrl?: string;
  description?: string;
}): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/self-register`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/** Registers an already-uploaded bucket object as an organization attachment. */
export const addOrganizationDocumentation = (
  code: string,
  body: IAddOrganizationDocumentationBody
): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const ACCEPTED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
];

export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Uploads an organization logo and saves it on the organization, returning the stored URL.
 *
 * Uses the public direct upload rather than the presigned flow the attachments use: `logoUrl` is
 * persisted and rendered in an <img>, so it needs a durable public URL, not a presigned one that
 * expires. This mirrors how the admin portal sets organization logos.
 */
export const uploadOrganizationLogo = async (
  code: string,
  file: File
): Promise<string> => {
  const logoUrl = await uploadFile(file, EUploadImageType.ORGANIZATION, code);
  await editOrganization(code, { logoUrl });
  return logoUrl;
};

/** A required onboarding document, paired with the file the user picked for it. */
export interface IOrganizationDocumentUpload {
  /** Machine identifier for the document slot (see REQUIRED_ORGANIZATION_DOCUMENTS). */
  type: string;
  /** Human-readable name — becomes the attachment title the admin reviewer sees. */
  title: string;
  file: File;
}

/**
 * Submits the onboarding documents for an organization, mirroring how the admin portal uploads
 * organization attachments: presigned PUT straight to the bucket, then register each object on
 * /organizations/{code}/attachments by bucket + key.
 *
 * Uploads run in parallel and the whole call rejects if any one fails, so the UI can surface a
 * single error. Note that leaves partial success possible — earlier documents may already be
 * registered when a later one fails. Re-submitting re-uploads everything, which the backend
 * treats as additional attachments rather than replacements.
 */
export const submitOrganizationDocuments = async (
  code: string,
  documents: IOrganizationDocumentUpload[]
): Promise<void> => {
  await Promise.all(
    documents.map(async ({ title, file }) => {
      const { bucket, key } = await uploadFileWithPresignedUrl(
        file,
        EUploadImageType.ORGANIZATION,
        code
      );
      return addOrganizationDocumentation(code, {
        title,
        bucketName: bucket,
        objectName: key,
        description: file.name,
      });
    })
  );
};

/**
 * Real-time, DB-backed organization state for the logged-in account (see useOrganizationState) —
 * the only reliable source for verificationStatus. organizationAdminCode/clientAdminClientId JWT
 * claims are set as soon as an account is linked to an org (not only once verified), so their
 * presence can't be used to infer approval status.
 */
export const getMyOrganization = (): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/my-organization`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};
